import type { ReactNode } from "react";
import { Badge } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { btnSlimCls, btnSlimPrimaryCls } from "components/tw/classes";
import type { FulfillmentRow } from "../fulfillmentSummary";
import { summaryRowTitle } from "../orderLineRows";
import { summaryRowStatus } from "../summaryRowStatus";

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className="mt-1 text-sm text-zinc-900">{children}</dd>
    </div>
  );
}

type Props = {
  rows: FulfillmentRow[];
  onReserve: (productId: string) => void;
  onShip: (productId: string) => void;
};

/**
 * The fulfillment summary on a phone: one card per order line, with the four
 * quantities under its name and the buttons under them. The table has seven
 * columns, which a phone shows only by scrolling the buttons out of sight.
 */
export default function FulfillmentSummaryCards({ rows, onReserve, onShip }: Props) {
  const { t } = useTranslation();
  return (
    <ul className="space-y-2">
      {rows.map((item) => {
        const status = summaryRowStatus(item);
        return (
          <li key={item.key} className="space-y-3 rounded-lg border border-zinc-200 p-3">
            <div>
              <p className="text-sm font-medium text-zinc-900">{summaryRowTitle(item, t)}</p>
              <p className="text-xs text-zinc-500">{item.sku}</p>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-center min-[480px]:grid-cols-4">
              <Figure label={t("ordered")}>{item.ordered}</Figure>
              <Figure label={t("reserved")}>
                <Badge tone={status.reservedTone}>{status.reservedText}</Badge>
              </Figure>
              <Figure label={t("pending")}>
                {status.pendingText ? <Badge tone="info">{status.pendingText}</Badge> : "-"}
              </Figure>
              <Figure label={t("shipped")}>
                <Badge tone={status.shippedTone}>{status.shippedText}</Badge>
              </Figure>
            </dl>
            {/* The table keeps this in a tooltip, which a touch screen never shows. */}
            {status.overshootTextKey && (
              <p className="text-xs text-amber-700">{t(status.overshootTextKey)}</p>
            )}
            {(status.canReserve || status.canShip || status.isComplete) && (
              <div className="flex flex-wrap gap-2">
                {status.canReserve && (
                  <button
                    type="button"
                    className={btnSlimCls}
                    onClick={() => onReserve(item.productId)}
                  >
                    {`+${t("reserve")}`}
                  </button>
                )}
                {status.canShip && (
                  <button
                    type="button"
                    className={btnSlimPrimaryCls}
                    onClick={() => onShip(item.productId)}
                  >
                    {`+${t("ship")}`}
                  </button>
                )}
                {status.isComplete && <Badge tone="success">{t("complete")}</Badge>}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
